import type { CSSProperties } from 'react';
import { cn } from '@/lib/utils';

// Base shimmer block — all skeleton pieces are built from this
const Shimmer = ({ className, style }: { className?: string; style?: CSSProperties }) => (
  <div className={cn('bg-slate-200 rounded animate-pulse', className)} style={style} />
);

// Mirrors MetricCard exactly: icon square + label + big value + subtitle
const SkeletonMetricCard = () => (
  <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-start gap-3">
    <Shimmer className="h-8 w-8 rounded-md shrink-0" />
    <div className="flex-1 min-w-0 space-y-2 pt-0.5">
      <Shimmer className="h-2.5 w-20 rounded" />
      <Shimmer className="h-7 w-16 rounded" />
      <Shimmer className="h-2 w-28 rounded" />
    </div>
  </div>
);

// Mirrors ClassificationFilters: label + 4 select-shaped pills
const SkeletonFilters = () => (
  <div className="flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
    <Shimmer className="h-3 w-14 rounded" />
    {Array.from({ length: 4 }).map((_, i) => (
      <Shimmer key={i} className="h-6 w-28 rounded" />
    ))}
  </div>
);

// Fake horizontal bars for layout="vertical" charts (ProductBarChart, etc.)
const SkeletonHorizontalChart = ({ rows = 6, title }: { rows?: number; title?: boolean }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-4">
    {title && <Shimmer className="h-3.5 w-32 rounded mb-4" />}
    <div className="space-y-3 mt-1">
      {Array.from({ length: rows }).map((_, i) => {
        // vary bar widths so it looks organic
        const widths = ['w-4/5', 'w-3/5', 'w-2/3', 'w-1/2', 'w-3/4', 'w-2/5', 'w-1/3'];
        return (
          <div key={i} className="flex items-center gap-3">
            <Shimmer className="h-2.5 w-24 shrink-0 rounded" />
            <Shimmer className={cn('h-4 rounded', widths[i % widths.length])} />
          </div>
        );
      })}
    </div>
  </div>
);

// Fake vertical bars for SpeciesBarChart
const SkeletonVerticalChart = () => (
  <div className="bg-white rounded-lg border border-slate-200 p-4">
    <Shimmer className="h-3.5 w-40 rounded mb-4" />
    <div className="flex items-end gap-3 h-44 px-4">
      {[60, 90, 45, 75, 55].map((h, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
          <Shimmer className="w-full rounded-t" style={{ height: `${h}%` }} />
          <Shimmer className="h-2 w-10 rounded" />
        </div>
      ))}
    </div>
  </div>
);

export const AnalyticsSkeleton = () => (
  <div className="space-y-5">
    {/* KPI cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <SkeletonMetricCard key={i} />
      ))}
    </div>

    {/* Filters */}
    <SkeletonFilters />

    {/* Species (vertical) + Products (horizontal) */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <SkeletonVerticalChart />
      <SkeletonHorizontalChart rows={6} title />
    </div>

    {/* Query Parameters — full width, more rows */}
    <SkeletonHorizontalChart rows={10} title />

    {/* Country + Top Users */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <SkeletonHorizontalChart rows={4} title />
      <SkeletonHorizontalChart rows={6} title />
    </div>
  </div>
);
